import React, { useState, useEffect } from 'react';
import { apiService, Procedure, LocalizedString, ProviderRole, resolveLocalized } from '../../services/apiService';

const ROLE_OPTIONS: Array<{ value: ProviderRole; label: string }> = [
  { value: 'dentist', label: 'Zahnarzt' },
  { value: 'hygienist', label: 'Hygiene' },
];

const PRESET_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280', '#F97316', '#14B8A6', '#FBBF24', '#A78BFA', '#60A5FA', '#DC2626'];
const LOCALES: Array<{ code: keyof LocalizedString; label: string }> = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
];

const emptyLocalized: LocalizedString = { de: '', en: '', es: '', ru: '' };

const emptyProcedure: Partial<Procedure> = {
  key: '',
  name: { ...emptyLocalized },
  description: { ...emptyLocalized },
  duration: 30,
  color: '#3B82F6',
  required_roles: ['dentist'],
  is_active: true,
  bookable: true,
};

const toLocalized = (v: any): LocalizedString => {
  if (!v) return { ...emptyLocalized };
  if (typeof v === 'string') return { ...emptyLocalized, de: v };
  return { de: v.de || '', en: v.en || '', es: v.es || '', ru: v.ru || '' };
};

const ProceduresPage: React.FC = () => {
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  type EditState = {
    id?: number;
    key?: string;
    name: LocalizedString;
    description: LocalizedString;
    duration?: number;
    color?: string;
    required_roles: ProviderRole[];
    is_active?: boolean;
    bookable?: boolean;
  };
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeLocale, setActiveLocale] = useState<keyof LocalizedString>('de');

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await apiService.getProcedures(false);
      setProcedures(r.data.data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load procedures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (p?: Procedure) => {
    if (p) {
      setEditing({
        id: p.id,
        key: p.key,
        name: toLocalized(p.name),
        description: toLocalized(p.description),
        duration: p.duration,
        color: p.color,
        required_roles: (p.required_roles && p.required_roles.length > 0) ? [...p.required_roles] : ['dentist'],
        is_active: p.is_active,
        bookable: p.bookable,
      });
    } else {
      setEditing({
        key: '',
        name: { ...emptyLocalized },
        description: { ...emptyLocalized },
        duration: 30,
        color: '#3B82F6',
        required_roles: ['dentist'],
        is_active: true,
        bookable: true,
      });
    }
    setActiveLocale('de');
  };

  const onSave = async () => {
    if (!editing) return;
    if (!editing.name.de) {
      setError('At least the German name is required');
      return;
    }
    if (!editing.required_roles || editing.required_roles.length === 0) {
      setError('Mindestens eine benötigte Rolle muss ausgewählt sein');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload: Partial<Procedure> = {
        key: editing.key,
        name: editing.name,
        description: editing.description,
        duration: editing.duration,
        color: editing.color,
        required_roles: editing.required_roles,
        is_active: editing.is_active,
        bookable: editing.bookable,
      };
      if (editing.id) {
        await apiService.updateProcedure(editing.id, payload);
      } else {
        await apiService.createProcedure(payload);
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p: Procedure) => {
    if (!window.confirm(`Delete procedure "${resolveLocalized(p.name)}"?`)) return;
    try {
      await apiService.deleteProcedure(p.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Procedures</h2>
        <button
          onClick={() => startEdit()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-navy-admin hover:bg-blue-900"
        >
          + Add Procedure
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded">{error}</div>}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : procedures.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No procedures yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name (DE)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Benötigt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bookable</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {procedures.map(p => (
                  <tr key={p.id} className={p.is_active ? '' : 'opacity-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-md">{resolveLocalized(p.name, 'de')}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{p.key || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{p.duration} min</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(p.required_roles && p.required_roles.length > 0 ? p.required_roles : ['dentist']).map(r => (
                          <span key={r} className={`px-2 py-0.5 text-xs rounded-full ${r === 'hygienist' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                            {ROLE_OPTIONS.find(o => o.value === r)?.label ?? r}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="inline-block w-6 h-6 rounded border border-gray-300" style={{ backgroundColor: p.color }} title={p.color} />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {p.bookable !== false
                        ? <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Public</span>
                        : <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">Internal</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {p.is_active
                        ? <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
                        : <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">Inactive</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm space-x-2 whitespace-nowrap">
                      <button onClick={() => startEdit(p)} className="text-blue-600 hover:text-blue-900">Edit</button>
                      <button onClick={() => onDelete(p)} className="text-red-600 hover:text-red-900">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">{editing.id ? 'Edit Procedure' : 'New Procedure'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key (technical identifier, e.g. <code className="text-xs">cleaning</code>)</label>
                <input type="text" value={editing.key || ''} onChange={e => setEditing({ ...editing, key: e.target.value })}
                  placeholder="e.g. cleaning, whitening, botox"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" />
              </div>

              {/* Locale tabs */}
              <div>
                <div className="flex border-b border-gray-200 mb-2">
                  {LOCALES.map(l => (
                    <button key={l.code} type="button"
                      onClick={() => setActiveLocale(l.code)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeLocale === l.code ? 'border-navy-admin text-navy-admin' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                      {l.label}
                      {!editing.name[l.code] && <span className="ml-1 text-red-400">●</span>}
                    </button>
                  ))}
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name ({LOCALES.find(l => l.code === activeLocale)?.label}){activeLocale === 'de' && ' *'}
                    </label>
                    <input type="text" value={editing.name[activeLocale] || ''}
                      onChange={e => setEditing({ ...editing, name: { ...editing.name, [activeLocale]: e.target.value } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description ({LOCALES.find(l => l.code === activeLocale)?.label})
                    </label>
                    <textarea rows={2} value={editing.description[activeLocale] || ''}
                      onChange={e => setEditing({ ...editing, description: { ...editing.description, [activeLocale]: e.target.value } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input type="number" min={5} step={5} value={editing.duration ?? 30}
                    onChange={e => setEditing({ ...editing, duration: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_COLORS.map(c => (
                      <button key={c} type="button"
                        onClick={() => setEditing({ ...editing, color: c })}
                        className={`w-8 h-8 rounded border-2 ${editing.color === c ? 'border-gray-900' : 'border-gray-300'}`}
                        style={{ backgroundColor: c }} title={c} />
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Benötigte Behandler-Rollen *</label>
                <div className="flex flex-wrap gap-3">
                  {ROLE_OPTIONS.map(opt => {
                    const checked = editing.required_roles.includes(opt.value);
                    return (
                      <label key={opt.value} className="flex items-center space-x-2 text-sm px-3 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={e => {
                            const next = e.target.checked
                              ? Array.from(new Set([...editing.required_roles, opt.value]))
                              : editing.required_roles.filter(r => r !== opt.value);
                            setEditing({ ...editing, required_roles: next });
                          }}
                        />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Diese Rolle(n) müssen zum Wunschtermin frei sein. Z. B. Zahnreinigung → Hygiene; Beratung/Behandlung → Zahnarzt.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center space-x-2 text-sm">
                  <input type="checkbox" checked={editing.bookable !== false}
                    onChange={e => setEditing({ ...editing, bookable: e.target.checked })} />
                  <span>Bookable on public site (seadent.es)</span>
                </label>
                <label className="flex items-center space-x-2 text-sm">
                  <input type="checkbox" checked={!!editing.is_active}
                    onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                  <span>Active</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6 sticky bottom-0 bg-white pt-3 border-t">
              <button onClick={() => setEditing(null)} disabled={saving}
                className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={onSave} disabled={saving}
                className="px-4 py-2 bg-navy-admin text-white rounded hover:bg-blue-900 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProceduresPage;
